<template>
  <div class="tw:bg-blue-200">
    <ContainerLayout class="tw:pb-12 tw:pt-6 tw:py-12 tw:px-6 tw:relative tw:flex-col">
      <div class="tw:flex tw:mb-2 tw:mb-0 tw:w-full tw:justify-end">
        <button
          class="tw:hover:cursor-pointer tw:md:absolute tw:top-6 tw:right-0 tw:flex tw:items-center tw:justify-center tw:w-10 tw:h-10 tw:rounded hover:tw:bg-gray-100 tw:transition-colors tw:justify-end tw:md:justify-center"
          data-cy="close-modal-button"
          type="button"
          aria-label="Close"
          @click="closeBanner"
        >
          <Close class="tw:size-3.5" />
        </button>
      </div>

      <div
        class="tw:w-full tw:flex tw:flex-col tw:landing:flex-row tw:items-start tw:landing:items-center tw:gap-y-11 tw:md:gap-y-[32px]"
      >
        <div class="tw:landing:w-3/5">
          <h1 class="tw:text-[32px] tw:leading-[40px] tw:md:text-[44px] tw:md:leading-[55px] tw:font-bold tw:mb-4">
            {{ $t('rc_welcome_banner_header') }}
          </h1>
          <ul class="tw:flex tw:flex-col tw:gap-y-1 tw:mb-6">
            <li v-for="(point, index) in points" :key="index" class="tw:flex tw:gap-2">
              <CheckmarkSimple class="tw:text-gray-800 tw:stroke-gray-800 tw:size-6" />
              {{ $t(point) }}
            </li>
          </ul>
          <div class="tw:flex tw:gap-x-4">
            <MainButton
              v-if="!isMobile && !isTourCompleted"
              data-testid="rate-connect-tour-cta"
              class="tw:hidden tw:md:flex"
              @click="beginTour"
            >
              {{ $t('rc_tour_start_cta') }}
            </MainButton>
            <GhostButton
              data-testid="rate-connect-video-cta"
              class="tw:flex tw:gap-x-2 tw:items-center tw:justify-center tw:leading-none"
              @click="openVideo"
            >
              <PlayIcon />
              {{ $t('rc_welcome_bannner_video_cta') }}
            </GhostButton>
          </div>
        </div>

        <div class="tw:landing:w-2/5 tw:flex tw:justify-center tw:landing:justify-end tw:w-full tw:landing:w-auto">
          <picture>
            <source media="(min-width: 1024px)" :srcset="getBannerImage('l')" />
            <source media="(min-width: 980px)" :srcset="getBannerImage('m')" />
            <source media="(min-width: 768px)" :srcset="getBannerImage('l')" />

            <img
              :src="getBannerImage('s')"
              :alt="$t('rc_banner_illustration_description')"
              class="tw:max-w-[280px] tw:md:max-w-[514px] tw:landing:max-w-[413px] tw:lg:max-w-[514px] tw:w-full tw:h-auto"
            />
          </picture>
        </div>
      </div>
    </ContainerLayout>
  </div>

  <VideoModal
    :isOpen="isVideoOpen"
    @closeModal="closeVideo"
    :title="$t('rc_promo_video_header')"
    itemKey="rateConnect"
    id="Rate Connect"
  />
</template>
<script setup>
import { computed, ref, watch } from 'vue'

import ContainerLayout from '@/layouts/ContainerLayout.vue'
import CheckmarkSimple from '@/components/icons/CheckmarkSimple.svg?component'
import GhostButton from '@/components/base/buttons/GhostButton.vue'
import MainButton from '@/components/base/buttons/MainButton.vue'
import PlayIcon from '@/components/icons/PlayIcon.svg?component'
import VideoModal from '@/components/rateConnect/VideoModal.vue'
import Close from '@/components/icons/Close.svg?component'
import appAnalytics from '@/utils/tracking'

import { storageSession } from '@/utils/storageService'
import { selectedAccommodationId, useUpdatePromotionStatus, useUserDetails } from '@/layouts/queries'
import { useTour } from '@/composables/useRateConnectTour'
import { RATE_CONNECT_TOUR_STEPS } from '@/constants/rateConnectTourSteps'
import { useIsMobile } from '@/composables/useIsMobile'
import { useSessionValues } from '@/composables/useSessionValues'

const isVideoOpen = ref(false)
const { userId, language } = useSessionValues()
const { isMobile } = useIsMobile()
const { startTour } = useTour()
const { updatePromotionStatus } = useUpdatePromotionStatus(userId)
const { userDetails } = useUserDetails(userId)
const currentLocation = computed(() => language.value.split('-')[1].toLowerCase())
const hasMarkedSeen = ref(false)

const welcomeBannerImages = import.meta.glob('/src/assets/img/rate-connect/welcome-banner/*.png', {
  eager: true,
  query: '?url',
  import: 'default',
})

const getBannerImage = size => {
  const path = `/src/assets/img/rate-connect/welcome-banner/welcome_banner_${size}_${currentLocation.value}.png`
  return (
    welcomeBannerImages[path] ||
    welcomeBannerImages[`/src/assets/img/rate-connect/welcome-banner/welcome_banner_${size}_us.png`] ||
    ''
  )
}

watch(
  userDetails,
  details => {
    if (hasMarkedSeen.value || !details) return

    const tourData = details.promotions?.find(item => item.promotionName === 'RC_PRODUCT_INTRO_TOUR_V1')
    const isCompleted = tourData?.isCompleted ?? false
    const isSeen = !!tourData?.firstSeenAt

    if (!isCompleted && !isSeen) {
      hasMarkedSeen.value = true
      updatePromotionStatus({
        promotionType: 'RC_PRODUCT_INTRO_TOUR_V1',
        status: 'SEEN',
      })
    }
  },
  { immediate: true }
)

const rateConnectTourData = computed(() =>
  userDetails.value?.promotions?.find(item => item.promotionName === 'RC_PRODUCT_INTRO_TOUR_V1')
)

const isTourCompleted = computed(() => rateConnectTourData.value?.isCompleted ?? false)

const points = computed(() => [
  'rc_welcome_banner_benefit_1',
  'rc_welcome_banner_benefit_2',
  'rc_welcome_banner_benefit_3',
])

const openVideo = () => {
  appAnalytics.track(appAnalytics.events.RC_WELCOME_BANNER_PLAY_VIDEO_CLICKED, {
    itemId: selectedAccommodationId.value,
  })
  isVideoOpen.value = true
}
const closeVideo = () => {
  appAnalytics.track(appAnalytics.events.RC_WELCOME_VIDEO_CLOSED, {
    itemId: selectedAccommodationId.value,
  })
  storageSession.removeItem('new_campaign_created')
  isVideoOpen.value = false
}

const closeBanner = () => {
  storageSession.removeItem('new_campaign_created')
  appAnalytics.track(appAnalytics.events.RC_WELCOME_BANNER_CLOSED, {
    itemId: selectedAccommodationId.value,
  })
}

const beginTour = () => {
  appAnalytics.track(appAnalytics.events.RC_TAKE_TOUR_CLICKED, {
    itemId: selectedAccommodationId.value,
  })
  storageSession.removeItem('new_campaign_created')

  updatePromotionStatus({
    promotionType: 'RC_PRODUCT_INTRO_TOUR_V1',
    status: 'STARTED',
  })

  startTour(RATE_CONNECT_TOUR_STEPS)
}
</script>
